import React, { useEffect, useMemo, useRef, useState } from 'react';
import AccountSwitcher from '../../../components/AccountSwitcher';
import { useAccounts } from '../../../context/AccountContext';
import { Card } from '../../../components/ui/Card';
import { Image, Play, Filter, Calendar, RefreshCw, Download, LineChart, Eye, BarChart3, MousePointerClick, ChevronRight, ChevronDown } from 'lucide-react';
import DateRangeFilter from '../../../components/filters/DateRangeFilter';
import { isDemoBrand } from '../../../utils/demoBrand';

export default function GdnCreatives() {
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('GOOGLE_ADS');
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('last30'); // today|yesterday|last7|last30|this_month|last_month|custom
  const [gaAccountFilter, setGaAccountFilter] = useState('all');
  const [gaAccounts, setGaAccounts] = useState([]);
  const [startDate, setStartDate] = useState(''); // YYYY-MM-DD (for custom)
  const [endDate, setEndDate] = useState('');
  const [assetType, setAssetType] = useState('all'); // image|video|text|all
  const [records, setRecords] = useState([]); // raw rows from API
  const [adIds, setAdIds] = useState([]); // unique ad ids
  const [selectedLevel, setSelectedLevel] = useState('ad'); // 'campaign' | 'ad_group' | 'ad'
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [selectedAdGroupId, setSelectedAdGroupId] = useState(null);
  const [selectedAdId, setSelectedAdId] = useState(null);
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [expandedAdGroups, setExpandedAdGroups] = useState(new Set());
  const fmt = (n) => new Intl.NumberFormat('tr-TR', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(n || 0));
  const [adMetricsMap, setAdMetricsMap] = useState({}); // ad_id -> {impressions, clicks, cost, conversions, conversion_value}
  const videoRef = useRef(null);
  // showCalendar state removed — <DateRangeFilter /> manages its own popover
  const DUMMY_BRAND_IDS = new Set(['9ad536a0-dbdf-4935-8730-f2c2029c6d52', '1c11acdd-b0f6-4c9f-b59d-92854b91e3bb', '2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a']);
  const isDummyBrand = (id) => DUMMY_BRAND_IDS.has(id) || isDemoBrand(id);
  const toYMD = (d) => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth()+1).padStart(2,'0');
    const day = String(d.getDate()).padStart(2,'0');
    return `${y}-${m}-${day}`;
  };

  // Placeholder load (we'll wire real data next)
  useEffect(() => {
    const fetchCreatives = async () => {
      const applyDummy = () => {
        const imagePool = ['/Post2121.png', '/Post1212112.png', '/Post21213131.png', '/5.png', '/White%20Purple%20Gradient%20Mobile%20Apps%20Presentation%20.png'];
        const dummyRecords = [];
        const ids = [];
        const dummyMetrics = {};
        const toCurrency = (n) => n; // values are already in TL
        for (let i = 1; i <= 15; i++) {
          const campaignId = `D-C-${i}`;
          const campaignName = i <= 5 ? 'AW 2025 Display - Prospecting' : (i <= 10 ? 'Q4 Retargeting - Cart Abandoners' : 'Brand & Awareness - Always On');
          const adGroupId = `D-AG-${i}`;
          const adGroupName = i % 2 === 0 ? 'Lookalike Purchasers 1%' : 'Fashion Interest • 25-44';
          const adId = `D-AD-${i}`;
          const img = imagePool[(i - 1) % imagePool.length];
          ids.push(adId);
          // multiple image variants per ad (3 variants)
          for (let v = 0; v < 3; v++) {
            const imgVariant = imagePool[(i - 1 + v) % imagePool.length];
            const variantLabel = String.fromCharCode(65 + v); // A, B, C
            dummyRecords.push({
              ad_id: adId,
              campaign_id: campaignId,
              campaign_name: campaignName,
              ad_group_id: adGroupId,
              ad_group_name: adGroupName,
              image_url: imgVariant,
              asset_name: `Creative ${i} - Variant ${variantLabel}`,
              final_url: 'https://heyglowy.com'
            });
          }
          // multiple text assets per ad (3 variants)
          const textVariants = [
            `Campaign ${i}: Limited-time offers • Free shipping over ₺${750 + i * 10}`,
            `Only today on Campaign ${i} — Extra 10% off in cart`,
            `New arrivals in Campaign ${i}. Don’t miss out!`
          ];
          textVariants.forEach((tv) => {
            dummyRecords.push({
              ad_id: adId,
              campaign_id: campaignId,
              campaign_name: campaignName,
              ad_group_id: adGroupId,
              ad_group_name: adGroupName,
              text: tv,
              asset_text: tv
            });
          });
          // metrics
          const impressions = 52000 + i * 4500;
          const clicks = 800 + i * 60;
          const cost = toCurrency(600 + i * 70);
          const conversions = 5 + (i % 6);
          const conversion_value = 15000 + i * 4000;
          dummyMetrics[adId] = { impressions, clicks, cost, conversions, conversion_value };
        }
        setRecords(dummyRecords);
        setAdIds(ids);
        setSelectedAdId(ids[0] || null);
        setSelectedLevel('ad');
        setAdMetricsMap(dummyMetrics);
      };
      try {
        setLoading(true);
        const brandId = localStorage.getItem('currentBrandId');
        if (dateRange === 'custom' && (!startDate || !endDate)) {
          setLoading(false);
          return;
        }
        // Always show dummy for specific demo brand
        if (isDummyBrand(brandId)) {
          applyDummy();
          setLoading(false);
          return;
        }
        const query = new URLSearchParams();
        query.set('dateRange', dateRange);
        if (dateRange === 'custom') { query.set('startDate', startDate); query.set('endDate', endDate); }
        if (gaAccountFilter !== 'all') query.set('accountId', gaAccountFilter);

        const res = await fetch(`https://api.glowytics.com/api/google-ads/gdn-assets-enriched?${query.toString()}`, {
          headers: { 'X-Brand-ID': brandId },
          credentials: 'include'
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (isDummyBrand(brandId) && (!Array.isArray(data) || data.length === 0)) {
          applyDummy();
          return; // skip metrics fetch for dummy
        }
        setRecords(Array.isArray(data) ? data : []);
        // unique ad ids (no metrics in this endpoint)
        const idsSet = new Set();
        (data || []).forEach(r => { if (r.ad_id) idsSet.add(r.ad_id); });
        const ids = Array.from(idsSet);
        setAdIds(ids);
        setSelectedAdId(ids[0] || null);
        setSelectedLevel('ad');

        // fetch ad-level performance metrics
        const mres = await fetch(`https://api.glowytics.com/api/google-ads/asset-metrics?${query.toString()}`, {
          headers: { 'X-Brand-ID': brandId },
          credentials: 'include'
        });
        if (mres.ok) {
          const mdata = await mres.json();
          const map = {};
          (mdata || []).forEach(row => {
            const id = row.ad_id;
            if (!id) return;
            if (!map[id]) map[id] = { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversion_value: 0 };
            map[id].impressions += Number(row.impressions || 0);
            map[id].clicks += Number(row.clicks || 0);
            map[id].cost += Number(row.cost || 0);
            map[id].conversions += Number(row.conversions || 0);
            map[id].conversion_value += Number(row.conversion_value || 0);
          });
          setAdMetricsMap(map);
          // sort adIds by cost desc
          const sorted = [...ids].sort((a, b) => (Number(map[b]?.cost || 0) - Number(map[a]?.cost || 0)));
          setAdIds(sorted);
          if (!sorted.includes(selectedAdId)) {
            setSelectedAdId(sorted[0] || null);
          }
        } else {
          setAdMetricsMap({});
        }
      } catch (e) {
        const brandId = localStorage.getItem('currentBrandId');
        console.warn('GDN creatives fetch failed:', e);
        if (isDummyBrand(brandId)) {
          applyDummy();
        } else {
          setRecords([]);
          setAdIds([]);
          setSelectedAdId(null);
          setAdMetricsMap({});
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCreatives();
  }, [dateRange, startDate, endDate, gaAccountFilter]);

  useEffect(() => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    fetch('https://api.glowytics.com/api/auth/connections', {
      credentials: 'include',
      headers: { 'X-Brand-ID': brandId }
    })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.connections || []);
        const ga = list.filter(c => c.platform === 'GOOGLE_ADS' && c.status === 'ACTIVE');
        setGaAccounts(ga);
      })
      .catch(() => {});
  }, []);

  // Group by ad_id for right pane
  const groupedByAdId = useMemo(() => {
    const map = new Map();
    (records || []).forEach(r => {
      const id = r.ad_id || 'unknown';
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(r);
    });
    return map;
  }, [records]);

  // Build Campaign -> Ad Group -> Ad hierarchy with costs for sorting
  const hierarchy = useMemo(() => {
    const byCampaign = new Map();
    (records || []).forEach(r => {
      const campId = r.campaign_id || 'unknown_campaign';
      const campName = r.campaign_name || 'Unknown Campaign';
      const agId = r.ad_group_id || 'unknown_ad_group';
      const agName = r.ad_group_name || 'Unknown Ad Group';
      const adId = r.ad_id;
      if (!byCampaign.has(campId)) byCampaign.set(campId, { id: campId, name: campName, adGroups: new Map(), adIds: new Set() });
      const camp = byCampaign.get(campId);
      if (!camp.adGroups.has(agId)) camp.adGroups.set(agId, { id: agId, name: agName, adIds: new Set() });
      const ag = camp.adGroups.get(agId);
      if (adId) {
        camp.adIds.add(adId);
        ag.adIds.add(adId);
      }
    });
    // attach metric totals
    const costOf = (ids) => ids.reduce((s, id) => s + Number((adMetricsMap[id] || {}).cost || 0), 0);
    const imprOf = (ids) => ids.reduce((s, id) => s + Number((adMetricsMap[id] || {}).impressions || 0), 0);
    const clickOf = (ids) => ids.reduce((s, id) => s + Number((adMetricsMap[id] || {}).clicks || 0), 0);

    const campaigns = Array.from(byCampaign.values()).map(c => {
      const adGroupArray = Array.from(c.adGroups.values()).map(g => {
        const adList = Array.from(g.adIds);
        return {
          id: g.id,
          name: g.name,
          adIds: adList.sort((a,b)=>Number(adMetricsMap[b]?.cost||0)-Number(adMetricsMap[a]?.cost||0)),
          metrics: { cost: costOf(adList), impressions: imprOf(adList), clicks: clickOf(adList) }
        };
      }).sort((a,b)=>b.metrics.cost - a.metrics.cost);
      const adList = Array.from(c.adIds);
      return {
        id: c.id,
        name: c.name,
        adGroups: adGroupArray,
        adIds: adList,
        metrics: { cost: costOf(adList), impressions: imprOf(adList), clicks: clickOf(adList) }
      };
    }).sort((a,b)=>b.metrics.cost - a.metrics.cost);

    return campaigns;
  }, [records, adMetricsMap]);

  const metrics = useMemo(() => {
    // Aggregate totals for the current selection scope
    let targetAdIds;
    if (selectedLevel === 'campaign' && selectedCampaignId) {
      const camp = hierarchy.find(c => c.id === selectedCampaignId);
      targetAdIds = camp ? camp.adGroups.flatMap(g => g.adIds) : [];
    } else if (selectedLevel === 'ad_group' && selectedAdGroupId) {
      const camp = hierarchy.find(c => c.adGroups.some(g => g.id === selectedAdGroupId));
      const ag = camp ? camp.adGroups.find(g => g.id === selectedAdGroupId) : null;
      targetAdIds = ag ? ag.adIds : [];
    } else if (selectedLevel === 'ad' && selectedAdId) {
      targetAdIds = [selectedAdId];
    } else {
      targetAdIds = adIds;
    }

    let impressions = 0, clicks = 0, spend = 0;
    targetAdIds.forEach(id => {
      const m = adMetricsMap[id];
      if (!m) return;
      impressions += Number(m.impressions || 0);
      clicks += Number(m.clicks || 0);
      spend += Number(m.cost || 0);
    });
    const cpc = clicks > 0 ? spend / clicks : 0;
    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
    const cpm = impressions > 0 ? (spend / impressions) * 1000 : 0;
    return { impressions, clicks, spend, cpc, ctr, cpm };
  }, [adIds, adMetricsMap, assetType, selectedLevel, selectedCampaignId, selectedAdGroupId, selectedAdId, hierarchy]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-sky-500 to-cyan-500 rounded-xl">
            <Image className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">GDN Creatives</h1>
            <p className="text-sm text-gray-500 mt-1">Display creatives, previews and performance</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {gaAccounts.length >= 2 && (
            <select
              value={gaAccountFilter}
              onChange={(e) => setGaAccountFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="all">All Accounts</option>
              {gaAccounts.map(acc => (
                <option key={acc.customerId} value={acc.customerId}>
                  {acc.nickname || acc.accountName || acc.customerId}
                </option>
              ))}
            </select>
          )}
          <DateRangeFilter
            value={dateRange}
            customStart={startDate ? new Date(startDate) : null}
            customEnd={endDate ? new Date(endDate) : null}
            onChange={(range, start, end) => {
              setDateRange(range);
              setStartDate(start ? toYMD(start) : '');
              setEndDate(end ? toYMD(end) : '');
            }}
            presets={['today', 'yesterday', 'last7', 'last30', 'thisMonth', 'lastMonth', 'custom']}
          />
          {/* Calendar + quick-select merged into <DateRangeFilter /> */}
          <select className="px-3 py-1.5 border rounded-lg text-sm" value={assetType} onChange={e => setAssetType(e.target.value)}>
            <option value="all">All types</option>
            <option value="image">Image</option>
            <option value="video">Video</option>
            <option value="text">Text</option>
          </select>
          <button className="p-2 rounded-xl hover:bg-gray-50 text-gray-600" title="Refresh"><RefreshCw className="w-5 h-5" /></button>
          <button className="flex items-center gap-2 px-3 py-2 text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100"><Download className="w-4 h-4" /><span>Export</span></button>
        </div>
      </div>

      {/* Filters moved into header */}

      {/* Summary */}
      <div className="grid grid-cols-6 gap-4">
        <Card className="p-4 rounded-xl"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center"><Eye className="w-5 h-5 text-blue-600"/></div><div><p className="text-sm text-gray-500">Impressions</p><p className="text-lg font-semibold">{metrics.impressions.toLocaleString('tr-TR')}</p></div></div></Card>
        <Card className="p-4 rounded-xl"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center"><MousePointerClick className="w-5 h-5 text-green-600"/></div><div><p className="text-sm text-gray-500">Clicks</p><p className="text-lg font-semibold">{metrics.clicks.toLocaleString('tr-TR')}</p></div></div></Card>
        <Card className="p-4 rounded-xl"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center"><BarChart3 className="w-5 h-5 text-indigo-600"/></div><div><p className="text-sm text-gray-500">Spend</p><p className="text-lg font-semibold">₺{metrics.spend.toLocaleString('tr-TR', {maximumFractionDigits: 0})}</p></div></div></Card>
        <Card className="p-4 rounded-xl"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center"><LineChart className="w-5 h-5 text-rose-600"/></div><div><p className="text-sm text-gray-500">CPC</p><p className="text-lg font-semibold">₺{metrics.cpc.toFixed(2)}</p></div></div></Card>
        <Card className="p-4 rounded-xl"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center"><LineChart className="w-5 h-5 text-purple-600"/></div><div><p className="text-sm text-gray-500">CPM</p><p className="text-lg font-semibold">₺{metrics.cpm.toFixed(2)}</p></div></div></Card>
        <Card className="p-4 rounded-xl"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><LineChart className="w-5 h-5 text-amber-600"/></div><div><p className="text-sm text-gray-500">CTR</p><p className="text-lg font-semibold">{metrics.ctr.toFixed(2)}%</p></div></div></Card>
      </div>

      {/* Main layout */}
      <div className="flex gap-6 flex-wrap xl:flex-nowrap">
        {/* Left: Campaigns > Ad Groups > Ads */}
        <div className="w-full lg:w-5/12 xl:w-4/12">
          <Card className="rounded-xl overflow-hidden">
            <div className="p-4 border-b bg-cyan-50 text-cyan-700 font-medium flex justify-between items-center">
              <span>Campaigns / Ad Groups / Ads</span>
              {!loading && <span className="text-xs text-cyan-600">Ads: {adIds.length}</span>}
            </div>
            {/* Metrics legend */}
            <div className="px-3 py-1 text-[10px] text-gray-500 flex items-center border-b bg-white/60">
              <div className="flex-1" />
              <div className="w-16 text-right">₺ Cost</div>
              <div className="w-16 text-right">Clicks</div>
              <div className="w-16 text-right">Impr</div>
              <div className="w-16 text-right">CPM</div>
            </div>
            <div className="max-h-[620px] overflow-y-auto">
              {loading ? (
                <div className="py-10 text-center text-gray-500">Loading…</div>
              ) : (
                <div className="p-3 space-y-2">
                  {hierarchy.map(camp => {
                    const opened = expandedCampaigns.has(camp.id);
                    return (
                      <div key={camp.id} className="rounded-lg">
                        <div className={`p-2 cursor-pointer flex items-center ${selectedLevel==='campaign'&&selectedCampaignId===camp.id?'bg-cyan-50':'hover:bg-gray-50'}`} onClick={()=>{ setSelectedLevel('campaign'); setSelectedCampaignId(camp.id); setSelectedAdGroupId(null); setSelectedAdId(null); }}>
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <button className="text-gray-600" onClick={(e)=>{e.stopPropagation(); const n=new Set(expandedCampaigns); if(opened){n.delete(camp.id);} else {n.add(camp.id);} setExpandedCampaigns(n);}}>
                              {opened ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                            <h4 className="font-medium text-xs leading-snug whitespace-normal line-clamp-2 break-words" title={camp.name}>{camp.name}</h4>
                          </div>
                          <div className="w-16 text-right text-[10px] text-gray-700">₺{fmt(camp.metrics.cost)}</div>
                          <div className="w-16 text-right text-[10px] text-gray-700">{fmt(camp.metrics.clicks)}</div>
                          <div className="w-16 text-right text-[10px] text-gray-700">{fmt(camp.metrics.impressions)}</div>
                          <div className="w-16 text-right text-[10px] text-gray-700">{camp.metrics.impressions > 0 ? '₺' + (camp.metrics.cost / camp.metrics.impressions * 1000).toFixed(2) : '-'}</div>
                        </div>
                        {opened && (
                          <div className="pl-3 ml-1 border-l space-y-1">
                            {camp.adGroups.map(ag => {
                              const agOpen = expandedAdGroups.has(ag.id);
                              return (
                                <div key={ag.id} className="rounded-lg">
                                  <div className={`p-1.5 pl-3 cursor-pointer flex items-center ${selectedLevel==='ad_group'&&selectedAdGroupId===ag.id?'bg-cyan-50':'hover:bg-gray-50'}`} onClick={()=>{ setSelectedLevel('ad_group'); setSelectedCampaignId(camp.id); setSelectedAdGroupId(ag.id); setSelectedAdId(null); }}>
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <button className="text-gray-600" onClick={(e)=>{e.stopPropagation(); const n=new Set(expandedAdGroups); if(agOpen){n.delete(ag.id);} else {n.add(ag.id);} setExpandedAdGroups(n);}}>
                                        {agOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                      </button>
                                      <span className="text-xs leading-snug whitespace-normal line-clamp-2 break-words" title={ag.name}>{ag.name}</span>
                                    </div>
                                    <div className="w-16 text-right text-[10px] text-gray-700">₺{fmt(ag.metrics.cost)}</div>
                                    <div className="w-16 text-right text-[10px] text-gray-700">{fmt(ag.metrics.clicks)}</div>
                                    <div className="w-16 text-right text-[10px] text-gray-700">{fmt(ag.metrics.impressions)}</div>
                                    <div className="w-16 text-right text-[10px] text-gray-700">{ag.metrics.impressions > 0 ? '₺' + (ag.metrics.cost / ag.metrics.impressions * 1000).toFixed(2) : '-'}</div>
                                  </div>
                                  {agOpen && (
                                    <div className="pl-6 ml-1 border-l space-y-1">
                                      {ag.adIds.map(id => {
                                        const m = adMetricsMap[id] || {};
                                        return (
                                          <div key={id} className={`p-1.5 rounded cursor-pointer ${selectedLevel==='ad'&&selectedAdId===id?'bg-cyan-50':'hover:bg-gray-50'}`} onClick={()=>{ setSelectedLevel('ad'); setSelectedCampaignId(camp.id); setSelectedAdGroupId(ag.id); setSelectedAdId(id); }}>
                                            <div className="flex items-center">
                                              <span className="text-xs leading-snug break-words flex-1" title={id}>{id}</span>
                                              <div className="w-16 text-right text-[10px] text-gray-700">₺{fmt(m.cost)}</div>
                                              <div className="w-16 text-right text-[10px] text-gray-700">{fmt(m.clicks)}</div>
                                              <div className="w-16 text-right text-[10px] text-gray-700">{fmt(m.impressions)}</div>
                                              <div className="w-16 text-right text-[10px] text-gray-700">{Number(m.impressions) > 0 ? '₺' + (Number(m.cost) / Number(m.impressions) * 1000).toFixed(2) : '-'}</div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Right: Assets under selected Ad */}
        <div className="w-full lg:w-7/12 xl:w-8/12">
          <div className="sticky top-6">
            <Card className="rounded-xl overflow-hidden">
              <div className="p-3 border-b bg-white text-cyan-700 font-medium">Asset Variants & Mappings</div>
              {(selectedLevel==='ad' && selectedAdId) || (selectedLevel==='ad_group' && selectedAdGroupId) || (selectedLevel==='campaign' && selectedCampaignId) ? (
                <div className="p-3 space-y-4">
                  {(() => {
                    let headerLabel = '';
                    let targetAdIds = [];
                    if (selectedLevel==='campaign') {
                      headerLabel = `Campaign: ${selectedCampaignId}`;
                      const camp = hierarchy.find(c=>c.id===selectedCampaignId);
                      targetAdIds = camp ? camp.adGroups.flatMap(g=>g.adIds) : [];
                    } else if (selectedLevel==='ad_group') {
                      headerLabel = `Ad Group: ${selectedAdGroupId}`;
                      const camp = hierarchy.find(c=>c.adGroups.some(g=>g.id===selectedAdGroupId));
                      const ag = camp ? camp.adGroups.find(g=>g.id===selectedAdGroupId) : null;
                      targetAdIds = ag ? ag.adIds : [];
                    } else {
                      headerLabel = `Ad ID: ${selectedAdId}`;
                      targetAdIds = selectedAdId ? [selectedAdId] : [];
                    }

                    const totals = targetAdIds.reduce((acc,id)=>{
                      const m = adMetricsMap[id] || {}; acc.impressions += Number(m.impressions||0); acc.clicks += Number(m.clicks||0); acc.cost += Number(m.cost||0); return acc;}, {impressions:0, clicks:0, cost:0});
                    const totalImpr = totals.impressions;
                    const totalClicks = totals.clicks;
                    const totalCost = totals.cost;
                    const cpc = totalClicks>0? totalCost/totalClicks:0;
                    return (
                      <div className="grid grid-cols-4 gap-3 text-xs">
                        <div><span className="text-gray-500">Scope:</span> <span className="font-medium">{headerLabel}</span></div>
                        <div><span className="text-gray-500">Impr:</span> {totalImpr.toLocaleString('tr-TR')}</div>
                        <div><span className="text-gray-500">Clicks:</span> {totalClicks.toLocaleString('tr-TR')}</div>
                        <div><span className="text-gray-500">Spend/CPC:</span> ₺{Math.round(totalCost).toLocaleString('tr-TR')} • ₺{cpc.toFixed(2)}</div>
                      </div>
                    );
                  })()}

                  {(() => {
                    let list = [];
                    if (selectedLevel==='campaign' && selectedCampaignId) {
                      list = (records || []).filter(r=>r.campaign_id===selectedCampaignId);
                    } else if (selectedLevel==='ad_group' && selectedAdGroupId) {
                      list = (records || []).filter(r=>r.ad_group_id===selectedAdGroupId);
                    } else if (selectedLevel==='ad' && selectedAdId) {
                      list = groupedByAdId.get(selectedAdId) || [];
                    }
                    const textAssets = list.filter(a => (a.text || '').trim() !== '');
                    const imageAssets = list.filter(a => !!a.image_url);
                    const videoAssets = list.filter(a => !!a.youtube_video_id);

                    return (
                      <div className="space-y-6">
                        {/* Image Assets FIRST */}
                        <div>
                          <div className="px-1 py-2 text-sm font-medium text-gray-700">Image Assets ({imageAssets.length})</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {imageAssets.length === 0 ? (
                              <div className="col-span-full text-xs text-gray-500 px-2">No image assets</div>
                            ) : imageAssets.map((r, idx) => {
                              const previewUrl = r.image_url || r.final_url;
                              return (
                                <div key={`i-${selectedAdId}-${idx}`} className="border rounded-lg overflow-hidden bg-white">
                                  <div className="relative bg-gray-100" style={{height:'160px'}}>
                                    {previewUrl ? (
                                      <img
                                        src={previewUrl}
                                        alt={r.asset_name || 'asset'}
                                        className="w-full h-full object-contain"
                                        loading="lazy"
                                        onError={(e)=>{
                                          const el = e.currentTarget;
                                          const fallbacks = ['/Post2121.png','/Post1212112.png','/Post21213131.png','/5.png','/White%20Purple%20Gradient%20Mobile%20Apps%20Presentation%20.png','/1.png','/2.png'];
                                          const idxAttr = el.getAttribute('data-fb-idx') || '0';
                                          let idx = Number(idxAttr);
                                          if (Number.isNaN(idx)) idx = 0;
                                          if (idx < fallbacks.length) {
                                            el.src = fallbacks[idx];
                                            el.setAttribute('data-fb-idx', String(idx + 1));
                                          } else {
                                            el.onerror = null;
                                            el.src = 'https://via.placeholder.com/600x400?text=Preview';
                                          }
                                        }}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-gray-400">No preview</div>
                                    )}
                                  </div>
                                  <div className="p-3 text-xs space-y-1">
                                    <div className="font-medium truncate" title={r.asset_name || r.asset_text}>{r.asset_name || r.asset_text || 'Unnamed asset'}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Text Assets SECOND */}
                        <div>
                          <div className="px-1 py-2 text-sm font-medium text-gray-700">Text Assets ({textAssets.length})</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {textAssets.length === 0 ? (
                              <div className="col-span-full text-xs text-gray-500 px-2">No text assets</div>
                            ) : textAssets.map((r, idx) => (
                              <div key={`t-${selectedAdId}-${idx}`} className="border rounded-lg bg-white p-3 text-xs space-y-2">
                                <div className="line-clamp-3 whitespace-pre-wrap">{r.text}</div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Video Assets LAST */}
                        <div>
                          <div className="px-1 py-2 text-sm font-medium text-gray-700">Video Assets ({videoAssets.length})</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {videoAssets.length === 0 ? (
                              <div className="col-span-full text-xs text-gray-500 px-2">No video assets</div>
                            ) : videoAssets.map((r, idx) => (
                              <div key={`v-${selectedAdId}-${idx}`} className="border rounded-lg overflow-hidden bg-white">
                                <div className="relative bg-gray-100" style={{height:'160px'}}>
                                  <div className="w-full h-full flex items-center justify-center text-gray-500">YouTube ID: {r.youtube_video_id}</div>
                                </div>
                                <div className="p-3 text-xs space-y-1">
                                  <div className="font-medium truncate" title={r.asset_name || r.asset_text}>{r.asset_name || r.asset_text || 'Unnamed asset'}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">Select an ad ID from the left list</div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}


